import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatUsd, formatDate, shortId } from '../lib/format';

interface OrderDetailResponse {
  order: {
    id: number;
    uuid: string;
    customer_email: string;
    customer_first_name: string | null;
    customer_last_name: string | null;
    customer_uuid: string;
    subtotal: number;
    tax: number;
    shipping_cost: number;
    total: number;
    status: string;
    payment_status: string;
    fulfillment_status: string;
    stripe_checkout_session_id: string | null;
    stripe_payment_intent_id: string | null;
    gelato_order_id: string | null;
    gelato_dispatch_pending: number;
    customer_notes: string | null;
    internal_notes: string | null;
    created_at: string;
    paid_at: string | null;
    shipped_at: string | null;
    completed_at: string | null;
    items: Array<{
      product_name: string;
      size_id: string;
      frame_id: string;
      quantity: number;
      unit_price: number;
      total_price: number;
      photo_src: string;
    }>;
    transactions: Array<{ uuid: string; type: string; amount: number; created_at: string }>;
    shipments: Array<{
      uuid: string;
      tracking_number: string | null;
      tracking_url: string | null;
      carrier: string | null;
      status: string;
      shipped_at: string | null;
      delivered_at: string | null;
    }>;
    shipping_address: {
      name: string;
      street1: string;
      street2: string | null;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    } | null;
  };
}

export function OrderDetail() {
  const { uuid } = useParams();
  const [data, setData] = useState<OrderDetailResponse | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get<OrderDetailResponse>(`/api/admin/orders/${uuid}`)
      .then((d) => {
        setData(d);
        setNotes(d.order.internal_notes || '');
      })
      .catch(console.error);

  useEffect(() => {
    load();
  }, [uuid]);

  if (!data) return <p>Loading…</p>;
  const o = data.order;

  const saveNotes = async () => {
    setBusy(true);
    try {
      await api.put(`/api/admin/orders/${o.uuid}`, { internal_notes: notes });
    } finally {
      setBusy(false);
    }
  };

  const retryGelato = async () => {
    setBusy(true);
    try {
      await api.post(`/api/admin/orders/${o.uuid}/dispatch`);
      await load();
    } catch (err) {
      alert(`Retry failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const markShipped = async () => {
    const trackingNumber = prompt('Tracking number (optional):') || '';
    const carrier = prompt('Carrier (optional):') || '';
    setBusy(true);
    try {
      await api.post(`/api/admin/orders/${o.uuid}/mark-shipped`, {
        tracking_number: trackingNumber || null,
        carrier: carrier || null,
      });
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Link to="/orders" style={{ color: '#6b7280', fontSize: 13 }}>
        ← Orders
      </Link>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 400, margin: '8px 0 4px' }}>
        Order #{shortId(o.uuid)}
      </h1>
      <p style={{ color: '#6b7280', margin: '0 0 24px' }}>
        Placed {formatDate(o.created_at)} · <Link to={`/customers/${o.customer_uuid}`}>{o.customer_email}</Link>
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
        <div>
          <Section title="Items">
            {o.items.map((it, i) => (
              <div
                key={i}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '60px 1fr 60px 90px',
                  gap: 16,
                  padding: '12px 0',
                  borderBottom: '1px solid #f0f0f0',
                  alignItems: 'center',
                }}
              >
                <img
                  src={it.photo_src}
                  alt=""
                  style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
                />
                <div>
                  <div>{it.product_name}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {it.size_id} · {it.frame_id}
                  </div>
                </div>
                <span style={{ textAlign: 'center' }}>×{it.quantity}</span>
                <span style={{ textAlign: 'right' }}>{formatUsd(it.total_price)}</span>
              </div>
            ))}
            <div style={{ marginTop: 16 }}>
              <Row label="Subtotal" value={formatUsd(o.subtotal)} />
              <Row label="Shipping" value={formatUsd(o.shipping_cost)} />
              <Row label="Tax" value={formatUsd(o.tax)} />
              <Row label="Total" value={formatUsd(o.total)} bold />
            </div>
          </Section>

          <Section title="Internal notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              style={{
                width: '100%',
                padding: 10,
                border: '1px solid #e5e5e5',
                borderRadius: 6,
                fontFamily: 'inherit',
                fontSize: 14,
              }}
            />
            <button onClick={saveNotes} disabled={busy} style={btnPrimary}>
              Save notes
            </button>
          </Section>
        </div>

        <div>
          <Section title="Status">
            <Row label="Order" value={o.status} />
            <Row label="Payment" value={o.payment_status} />
            <Row label="Fulfillment" value={o.fulfillment_status} />
            <Row label="Paid" value={formatDate(o.paid_at)} />
            <Row label="Shipped" value={formatDate(o.shipped_at)} />
            {o.gelato_dispatch_pending === 1 && (
              <div style={{ marginTop: 12 }}>
                <p style={{ color: '#b91c1c', fontSize: 13, margin: '0 0 8px' }}>
                  Gelato dispatch failed.
                </p>
                <button onClick={retryGelato} disabled={busy} style={btnPrimary}>
                  Retry Gelato
                </button>
              </div>
            )}
            {o.status === 'processing' && o.gelato_dispatch_pending !== 1 && (
              <button onClick={markShipped} disabled={busy} style={{ ...btnSecondary, marginTop: 12 }}>
                Mark shipped manually
              </button>
            )}
          </Section>

          <Section title="Shipping address">
            {o.shipping_address ? (
              <address style={{ fontStyle: 'normal', fontSize: 14, lineHeight: 1.6 }}>
                {o.shipping_address.name}
                <br />
                {o.shipping_address.street1}
                {o.shipping_address.street2 && (
                  <>
                    <br />
                    {o.shipping_address.street2}
                  </>
                )}
                <br />
                {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.postal_code}
                <br />
                {o.shipping_address.country}
              </address>
            ) : (
              <p style={{ color: '#6b7280' }}>No shipping address recorded.</p>
            )}
          </Section>

          <Section title="External references">
            {o.stripe_checkout_session_id && (
              <Row label="Stripe session" value={o.stripe_checkout_session_id.slice(0, 18) + '…'} />
            )}
            {o.gelato_order_id && <Row label="Gelato order" value={o.gelato_order_id} />}
          </Section>

          {o.shipments.length > 0 && (
            <Section title="Shipments">
              {o.shipments.map((s) => (
                <div key={s.uuid} style={{ marginBottom: 8 }}>
                  <Row label="Carrier" value={s.carrier || '—'} />
                  <Row label="Tracking" value={s.tracking_number || '—'} />
                  <Row label="Status" value={s.status} />
                  {s.tracking_url && (
                    <a href={s.tracking_url} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>
                      Track →
                    </a>
                  )}
                </div>
              ))}
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, padding: 20, marginBottom: 16 }}>
      <h2
        style={{
          margin: '0 0 12px',
          fontSize: 12,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: '#6b7280',
        }}
      >
        {title}
      </h2>
      {children}
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '4px 0',
        fontSize: 14,
        fontWeight: bold ? 600 : 400,
      }}
    >
      <span style={{ color: bold ? '#1a1a1a' : '#6b7280' }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

const btnPrimary: React.CSSProperties = {
  padding: '8px 14px',
  background: '#1a1a1a',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
  marginTop: 8,
};

const btnSecondary: React.CSSProperties = {
  padding: '8px 14px',
  background: '#fff',
  color: '#1a1a1a',
  border: '1px solid #e5e5e5',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
};
